import {ScaleLinear} from 'd3';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkHighlightRegion,
  HIGHLIGHT_STROKE_OPACITY,
  HIGHLIGHT_STROKE_WIDTH,
} from 'molbio/maps/BiointelligentLinksConstants';
import {
  getFillColor,
  getFillOpacity,
  getRectangularPath,
  getStrokeColor,
  getTopLineColor,
} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import LegacyNucleotidePartsView from 'molbio/maps/linear/LegacyNucleotidePartsView';
import {useRedraw} from 'molbio/maps/linear/useRedraw';
import {PART_NAME_FONT_SIZE} from 'molbio/maps/PartsViewConstants';
import {
  PartHighlightRegion,
  splitNucleotidePartModelsIntoHighlightRegions,
  splitProteinPartModelsIntoHighlightRegions,
} from 'molbio/maps/translateLinksToHighlightRegions';
import Type from 'molbio/maps/Type';
import Protein from 'molbio/proteins/Protein';

export const TOP_LINE_HEIGHT = 5;
// Number of pixels to show part background above and below the residues
const PROTEIN_PART_RESIDUE_MARGIN = 6;

function getPartLinkRegionsForSlice(
  sliceStart: number,
  sliceEnd: number,
  polymer: Sequence | Protein,
  linkType: BiointelligentLinkType
): Array<BiointelligentLinkHighlightRegion> {
  const polymerLength = polymer.length();
  // Sometimes, we have extra slices past the end of the sequence. This causes warnings when getting the
  // relevant links, so we early return in those cases, and clamp the end such that start/end is always in
  // the [0, seqLength] range.
  if (sliceStart > polymerLength) {
    return [];
  }
  const clampedEnd = Math.min(sliceEnd, polymerLength);
  switch (linkType) {
    case BiointelligentLinkType.NUCLEOTIDE_PART:
      return splitNucleotidePartModelsIntoHighlightRegions(
        (polymer as Sequence).parts().getSequencePartsBetween(sliceStart, clampedEnd),
        polymer as Sequence
      );
    case BiointelligentLinkType.AMINO_ACID_PART:
      return splitProteinPartModelsIntoHighlightRegions(
        (polymer as Protein).getPartsBetween(sliceStart, clampedEnd),
        polymerLength
      );
    default:
      assert(false, 'BiointelligentLinkHighlightView does not yet support anything other than part links.');
  }
}

function getHeight(dnaHeight: number, dnaSpacer: number, dnaComplement: boolean): number {
  if (dnaComplement) {
    return 2 * (dnaHeight + dnaSpacer);
  } else {
    return dnaHeight + dnaSpacer;
  }
}

function calculatePartDimensions(
  polymerHeight: number,
  polymerSpacer: number,
  complementVisible: boolean,
  linkType: BiointelligentLinkType
): {
  partFontType: Type;
  partBoxHeight: number;
  backgroundY: number;
  height: number;
} {
  const partFontType = new Type(PART_NAME_FONT_SIZE);
  switch (linkType) {
    case BiointelligentLinkType.NUCLEOTIDE_PART:
      return {
        partFontType,
        partBoxHeight: partFontType.height() + 2,
        backgroundY: partFontType.height() + 2,
        height: getHeight(polymerHeight, polymerSpacer, complementVisible),
      };
    case BiointelligentLinkType.AMINO_ACID_PART:
      return {
        partFontType,
        partBoxHeight: TOP_LINE_HEIGHT + PROTEIN_PART_RESIDUE_MARGIN,
        backgroundY: 0,
        height: polymerHeight + polymerSpacer + PROTEIN_PART_RESIDUE_MARGIN,
      };
    default:
      assert(false, 'BiointelligentLinkHighlightView does not yet support anything other than part links.');
  }
}

interface BiointelligentLinkHighlightViewProps {
  /** A function that takes the index of a residue and returns its x-axis position in pixels on the slice */
  xFn: ScaleLinear<number, number>;
  /** The residue index at which the slice starts */
  sliceStart: number;
  /** The residue index at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence or protein being rendered */
  polymer: Sequence | Protein;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Height in pixels of the residues at default zoom. */
  polymerHeight: number;
  /** Height in pixels of the space below the residues at default zoom. */
  polymerSpacer: number;
  /** Whether or not the complement slice is visible */
  complementVisible: boolean;
  /** What kind of map we're drawing on */
  mapType: SliceMapType;
  /**
   * A function that accepts a new box height or a way to calculate it and triggers a redraw with the new box
   * height.
   * */
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
  /** The type of link being rendered */
  linkType: BiointelligentLinkType.AMINO_ACID_PART | BiointelligentLinkType.NUCLEOTIDE_PART;
}

export default function BiointelligentLinkHighlightView(
  props: BiointelligentLinkHighlightViewProps
): JSX.Element {
  const {partFontType, partBoxHeight, backgroundY, height} = calculatePartDimensions(
    props.polymerHeight,
    props.polymerSpacer,
    props.complementVisible,
    props.linkType
  );
  const topLineY = backgroundY - TOP_LINE_HEIGHT;

  const linkRegions = getPartLinkRegionsForSlice(
    props.sliceStart,
    props.sliceEnd,
    props.polymer,
    props.linkType
  );

  useRedraw({
    redraw: props.redraw,
    shouldRedraw: () => linkRegions.length > 0,
    newBoxHeight: partBoxHeight,
  });

  if (
    props.linkType === BiointelligentLinkType.NUCLEOTIDE_PART &&
    !BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS
  ) {
    return (
      <LegacyNucleotidePartsView
        polymerLayout={props.polymerLayout}
        partRegions={linkRegions as Array<PartHighlightRegion>}
        xFn={props.xFn}
        sliceStart={props.sliceStart}
        sliceEnd={props.sliceEnd}
        backgroundY={backgroundY}
        height={height}
        sequenceLength={props.polymer.length()}
        type={partFontType}
        mapType={props.mapType}
      />
    );
  }

  return (
    <ComponentWrapper layout={props.polymerLayout} shouldStopMouseDownPropagation={false}>
      <>
        {linkRegions.map((linkRegion: BiointelligentLinkHighlightRegion, index: number) => (
          <g key={`${props.sliceStart}-${props.sliceEnd}-${index}`}>
            <path
              d={getRectangularPath(
                linkRegion,
                props.sliceStart,
                props.sliceEnd,
                props.xFn,
                backgroundY,
                height,
                props.polymer.length()
              )}
              stroke={getStrokeColor()}
              strokeWidth={HIGHLIGHT_STROKE_WIDTH}
              strokeOpacity={HIGHLIGHT_STROKE_OPACITY}
              fill={getFillColor(linkRegion)}
              fillOpacity={getFillOpacity(linkRegion, true)}
            />
            <path
              d={getRectangularPath(
                linkRegion,
                props.sliceStart,
                props.sliceEnd,
                props.xFn,
                topLineY,
                TOP_LINE_HEIGHT,
                props.polymer.length()
              )}
              fill={getTopLineColor(linkRegion)}
              fillOpacity={getFillOpacity(linkRegion, true)}
            />
          </g>
        ))}
      </>
    </ComponentWrapper>
  );
}
